import { Loader2, Zap } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { RestingOrderView } from '../hooks/useRestingOrder';

import { limitDistanceLabel } from './LimitOrderControls';

/** "14:23:41 · 19 Jul", the same stamp the position overview uses. */
function fmtPlacedAt(timestamp: number | null): string {
    if (timestamp === null || !Number.isFinite(timestamp)) return '—';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '—';
    return `${date.toLocaleTimeString('en-GB', {
        hour12: false,
    })} · ${date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
    })}`;
}

const Row: React.FC<{ label: string; value: string; valueClass?: string }> = ({
    label,
    value,
    valueClass,
}) => (
    <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span
            className={cn(
                'font-mono text-[11px] font-medium tabular-nums text-foreground/90',
                valueClass,
            )}
        >
            {value}
        </span>
    </div>
);

/**
 * The order form's replacement while a limit order rests on the charted market.
 *
 * A resting order is NOT a position — there is no entry, no PnL and nothing to
 * close — so it gets neither the order form (the pair's slot is taken) nor the
 * position view (there is nothing to show PnL for). It gets its own card with
 * the only two moves that exist: drop it, or take it at market. The moment it
 * fills, the ordinary position view takes over with no extra work.
 */
const RestingOrderCard: React.FC<{
    order: RestingOrderView;
    markPrice: number;
    formatPrice: (value: number) => string;
    onCancel: () => void;
    onFill: () => void;
    busy?: boolean;
    compact?: boolean;
}> = ({ order, markPrice, formatPrice, onCancel, onFill, busy, compact }) => {
    const isLong = order.type === 'LONG';
    const distance = limitDistanceLabel(order.limitPrice, markPrice);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-secondary/30 p-3">
                <div className="mb-0.5 flex items-center justify-between">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Limit order
                    </span>
                    <span
                        className={cn(
                            'rounded-full px-2 py-0.5 text-[9px] font-bold uppercase',
                            isLong
                                ? 'bg-primary/15 text-primary'
                                : 'bg-destructive/15 text-destructive',
                        )}
                    >
                        {isLong ? '▲ Long' : '▼ Short'}
                        {order.leverage !== null && ` ${order.leverage}x`}
                    </span>
                </div>
                <Row label="Market" value={order.coin} />
                <Row
                    label="Limit price"
                    value={
                        order.limitPrice !== null
                            ? `$${formatPrice(order.limitPrice)}`
                            : '—'
                    }
                    valueClass="font-semibold"
                />
                <Row
                    label="Mark price"
                    value={markPrice > 0 ? `$${formatPrice(markPrice)}` : '—'}
                />
                <Row label="Distance" value={distance ?? '—'} />
                <Row
                    label="Margin"
                    value={
                        order.amount !== null
                            ? `$${order.amount.toFixed(2)}`
                            : '—'
                    }
                />
                <Row
                    label="Resting since"
                    value={fmtPlacedAt(order.placedAt)}
                />
            </div>

            <p className="text-center text-[11px] text-muted-foreground">
                Waiting for the market to reach your price. Nothing is open yet.
            </p>

            <div className="grid grid-cols-2 gap-2">
                <Button
                    variant="outline"
                    size={compact ? 'default' : 'lg'}
                    onClick={onCancel}
                    disabled={busy}
                    className="w-full gap-2"
                >
                    {busy ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <span className="text-xs font-bold uppercase">
                            Cancel order
                        </span>
                    )}
                </Button>
                <Button
                    variant="cyber-long"
                    size={compact ? 'default' : 'lg'}
                    onClick={onFill}
                    disabled={busy}
                    className="st-close-flat w-full gap-2"
                >
                    {busy ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <>
                            <Zap size={14} />
                            <span className="text-xs font-bold uppercase">
                                Fill now
                            </span>
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default RestingOrderCard;
